import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  fetchPublicMessages,
  postPublicMessage,
  fetchChatContacts,
  fetchPrivateMessages,
  postPrivateMessage
} from '../../services/api';

const MAX_PUBLIC_MESSAGES = 50;
const MAX_PRIVATE_MESSAGES = 50;
const POLL_INTERVAL_MS = 2000;
const LAST_SEEN_PUBLIC_KEY = 'chat_last_seen_public';
const LAST_SEEN_PRIVATE_PREFIX = 'chat_last_seen_private_';

const ChatWidget = ({ user }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [mode, setMode] = useState(user ? 'private' : 'public');
  const [contacts, setContacts] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [publicMessages, setPublicMessages] = useState([]);
  const [privateMessages, setPrivateMessages] = useState([]);
  const [messageText, setMessageText] = useState('');
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const formatTimestamp = useCallback((value) => {
    if (!value) {
      return '';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '';
    }
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }, []);

  const getLastSeenPublic = useCallback(() => {
    return localStorage.getItem(LAST_SEEN_PUBLIC_KEY);
  }, []);

  const setLastSeenPublic = useCallback((value) => {
    if (value) {
      localStorage.setItem(LAST_SEEN_PUBLIC_KEY, value);
    }
  }, []);

  const getLastSeenPrivate = useCallback((contactId) => {
    if (!contactId) {
      return null;
    }
    return localStorage.getItem(`${LAST_SEEN_PRIVATE_PREFIX}${contactId}`);
  }, []);

  const setLastSeenPrivate = useCallback((contactId, value) => {
    if (!contactId || !value) {
      return;
    }
    localStorage.setItem(`${LAST_SEEN_PRIVATE_PREFIX}${contactId}`, value);
  }, []);

  const publicEndRef = useRef(null);
  const privateEndRef = useRef(null);
  const publicContainerRef = useRef(null);
  const privateContainerRef = useRef(null);

  useEffect(() => {
    if (!user) {
      setMode('public');
    }
  }, [user]);

  const scrollToBottom = useCallback((ref) => {
    if (ref.current) {
      ref.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  const isNearBottom = useCallback((containerRef) => {
    const container = containerRef.current;
    if (!container) {
      return true;
    }
    const threshold = 40;
    return container.scrollTop + container.clientHeight >= container.scrollHeight - threshold;
  }, []);

  useEffect(() => {
    if (isNearBottom(publicContainerRef)) {
      scrollToBottom(publicEndRef);
    }
  }, [publicMessages, scrollToBottom, isNearBottom]);

  useEffect(() => {
    if (isNearBottom(privateContainerRef)) {
      scrollToBottom(privateEndRef);
    }
  }, [privateMessages, scrollToBottom, isNearBottom]);

  const loadPublicMessages = useCallback(async () => {
    setLoadingMessages(true);
    setErrorMessage('');
    try {
      const messages = await fetchPublicMessages(MAX_PUBLIC_MESSAGES);
      setPublicMessages(messages || []);
      if (isOpen && mode === 'public' && messages && messages.length > 0) {
        const latest = messages[messages.length - 1]?.created_at;
        if (latest) {
          setLastSeenPublic(latest);
        }
      }
    } catch (error) {
      setErrorMessage('Unable to load public chat.');
    } finally {
      setLoadingMessages(false);
    }
  }, [isOpen, mode, setLastSeenPublic]);

  const loadContacts = useCallback(async () => {
    if (!user) {
      setContacts([]);
      setSelectedContact(null);
      return;
    }

    setLoadingContacts(true);
    setErrorMessage('');
    try {
      const list = await fetchChatContacts();
      const sorted = [...(list || [])].sort((a, b) => {
        if (a.last_message_at && b.last_message_at) {
          return new Date(b.last_message_at) - new Date(a.last_message_at);
        }
        if (a.last_message_at) {
          return -1;
        }
        if (b.last_message_at) {
          return 1;
        }
        return a.username.localeCompare(b.username);
      });
      setContacts(sorted);
      if (sorted.length > 0) {
        const stillExists = selectedContact && sorted.some((contact) => contact.id === selectedContact.id);
        if (!stillExists) {
          setSelectedContact(sorted[0]);
        }
      } else {
        setSelectedContact(null);
      }
    } catch (error) {
      setErrorMessage('Unable to load contacts.');
    } finally {
      setLoadingContacts(false);
    }
  }, [user, selectedContact]);

  const loadPrivateMessages = useCallback(async (contactId) => {
    if (!contactId) {
      setPrivateMessages([]);
      return;
    }

    setLoadingMessages(true);
    setErrorMessage('');
    try {
      const messages = await fetchPrivateMessages(contactId, MAX_PRIVATE_MESSAGES);
      setPrivateMessages(messages || []);
      if (isOpen && mode === 'private' && messages && messages.length > 0) {
        const latest = messages[messages.length - 1]?.created_at;
        if (latest) {
          setLastSeenPrivate(contactId, latest);
        }
      }
    } catch (error) {
      setErrorMessage('Unable to load private chat.');
    } finally {
      setLoadingMessages(false);
    }
  }, [isOpen, mode, setLastSeenPrivate]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    if (mode === 'public') {
      loadPublicMessages();
    } else if (mode === 'private') {
      loadContacts();
      setPrivateMessages([]);
    }
  }, [isOpen, mode, loadPublicMessages, loadContacts]);

  useEffect(() => {
    if (!isOpen || mode !== 'private') {
      return;
    }

    if (selectedContact) {
      loadPrivateMessages(selectedContact.id);
    }
  }, [isOpen, mode, selectedContact, loadPrivateMessages]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      loadPublicMessages();
      if (user) {
        loadContacts();
      }
      if (isOpen && mode === 'private' && selectedContact) {
        loadPrivateMessages(selectedContact.id);
      }
    }, POLL_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [isOpen, mode, selectedContact, loadPublicMessages, loadPrivateMessages, loadContacts, user]);

  const handleSendMessage = async () => {
    const content = messageText.trim();
    if (!content) {
      return;
    }

    setErrorMessage('');
    setMessageText('');

    try {
      if (mode === 'public') {
        await postPublicMessage(content);
        await loadPublicMessages();
      } else if (mode === 'private' && selectedContact) {
        await postPrivateMessage(selectedContact.id, content);
        await loadPrivateMessages(selectedContact.id);
      }
    } catch (error) {
      setErrorMessage('Unable to send message.');
    }
  };

  const showPrivate = user && mode === 'private';
  const headerTitle = useMemo(() => {
    if (!user) {
      return 'Public chat';
    }
    return mode === 'private' ? 'Private chat' : 'Public chat';
  }, [user, mode]);

  const unreadPublicCount = useMemo(() => {
    if (!publicMessages || publicMessages.length === 0) {
      return 0;
    }
    const lastSeen = getLastSeenPublic();
    const latest = publicMessages[publicMessages.length - 1]?.created_at;
    if (!latest) {
      return 0;
    }
    if (!lastSeen) {
      return 1;
    }
    return new Date(latest) > new Date(lastSeen) ? 1 : 0;
  }, [publicMessages, getLastSeenPublic]);

  const unreadContactsCount = useMemo(() => {
    if (!user) {
      return 0;
    }
    return contacts.reduce((count, contact) => {
      if (!contact.last_message_at || !contact.last_message_sender_id) {
        return count;
      }
      if (contact.last_message_sender_id === user.id) {
        return count;
      }
      const lastSeen = getLastSeenPrivate(contact.id);
      if (!lastSeen) {
        return count + 1;
      }
      return new Date(contact.last_message_at) > new Date(lastSeen) ? count + 1 : count;
    }, 0);
  }, [contacts, getLastSeenPrivate, user]);

  const unreadTotal = unreadPublicCount + unreadContactsCount;

  return (
    <>
      {isOpen && (
        <div className="position-fixed bottom-0 end-0 m-4 shadow-lg bg-white border rounded d-flex flex-column" style={{ width: '360px', height: '460px', zIndex: 1050 }}>
          <div className="d-flex align-items-center justify-content-between px-3 py-2 border-bottom bg-light">
            <strong>{headerTitle}</strong>
            <button className="btn btn-sm btn-outline-secondary" onClick={() => setIsOpen(false)}>Close</button>
          </div>
          <div className="px-3 py-2 border-bottom">
            {user ? (
              <div className="btn-group w-100" role="group">
                <button
                  type="button"
                  className={`btn btn-sm ${mode === 'public' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setMode('public')}
                >
                  Public
                </button>
                <button
                  type="button"
                  className={`btn btn-sm ${mode === 'private' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setMode('private')}
                >
                  Private
                </button>
              </div>
            ) : (
              <div className="text-muted small">Login required for private chat.</div>
            )}
          </div>
          <div className="flex-grow-1 overflow-hidden">
            {errorMessage && (
              <div className="text-center text-danger small mt-2">{errorMessage}</div>
            )}
            {showPrivate ? (
              <div className="h-100 d-flex">
                <div className="border-end" style={{ width: '35%', overflowY: 'auto' }}>
                  {contacts.length === 0 && (
                    <div className="text-muted small p-2">No contacts available.</div>
                  )}
                  <div className="list-group list-group-flush">
                    {contacts.map((contact) => (
                      <button
                        key={contact.id}
                        className={`list-group-item list-group-item-action ${selectedContact && selectedContact.id === contact.id ? 'active' : ''}`}
                        onClick={() => setSelectedContact(contact)}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span>{contact.username}</span>
                          {contact.last_message_at && contact.last_message_sender_id !== user.id && (() => {
                            const lastSeen = getLastSeenPrivate(contact.id);
                            const hasUnread = !lastSeen || new Date(contact.last_message_at) > new Date(lastSeen);
                            return hasUnread ? <span className="badge bg-danger ms-2">!</span> : null;
                          })()}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
                <div className="flex-grow-1 d-flex flex-column">
                  <div ref={privateContainerRef} className="flex-grow-1 overflow-auto px-3 py-2">
                    {selectedContact ? (
                      privateMessages.map((msg) => (
                        <div key={msg.id} className={`mb-2 ${msg.sender_id === user.id ? 'text-end' : ''}`}>
                          <div className={`d-inline-block px-2 py-1 rounded ${msg.sender_id === user.id ? 'bg-primary text-white' : 'bg-light'}`}>
                            {msg.content}
                          </div>
                          <div className="small text-muted mt-1">
                            {formatTimestamp(msg.created_at)}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-muted small">Select a contact to start chatting.</div>
                    )}
                    <div ref={privateEndRef} />
                  </div>
                </div>
              </div>
            ) : (
              <div ref={publicContainerRef} className="h-100 overflow-auto px-3 py-2">
                {publicMessages.map((msg) => (
                  <div key={msg.id} className="mb-2">
                    <div className="small text-muted d-flex justify-content-between">
                      <span>{msg.sender_name || 'Guest'}</span>
                      <span>{formatTimestamp(msg.created_at)}</span>
                    </div>
                    <div className="bg-light rounded px-2 py-1">{msg.content}</div>
                  </div>
                ))}
                <div ref={publicEndRef} />
              </div>
            )}
          </div>
          <div className="border-top p-2">
            <div className="input-group">
              <input
                type="text"
                className="form-control"
                placeholder={showPrivate && !selectedContact ? 'Select a contact...' : 'Type a message...'}
                value={messageText}
                onChange={(event) => setMessageText(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    handleSendMessage();
                  }
                }}
                disabled={showPrivate && !selectedContact}
              />
              <button
                className="btn btn-primary"
                onClick={handleSendMessage}
                disabled={showPrivate && !selectedContact}
              >
                Send
              </button>
            </div>
          </div>
        </div>
      )}
      {isOpen && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100"
          style={{ zIndex: 1030 }}
          onClick={() => setIsOpen(false)}
          aria-hidden="true"
        />
      )}
      {!isOpen && (
        <button
          className="btn btn-primary position-fixed bottom-0 m-4 rounded-circle shadow position-relative"
          style={{ width: '56px', height: '56px', zIndex: 1040, right: '1.5rem' }}
          onClick={() => setIsOpen(true)}
          aria-label="Open chat"
        >
          <svg
            aria-hidden="true"
            viewBox="0 0 24 24"
            width="24"
            height="24"
            fill="currentColor"
          >
            <path d="M4 4h16a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H9.5L5 21.5V17H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2Z" />
          </svg>
          {unreadTotal > 0 && (
            <span
              className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger"
              style={{ fontSize: '0.6rem' }}
            >
              {unreadTotal}
            </span>
          )}
        </button>
      )}
    </>
  );
};

export default ChatWidget;
